import { Link } from 'react-router-dom';
import { getResponsiveImage } from '@/lib/imageUtils';

type CategoryColor = 'green' | 'pink' | 'purple' | 'blue' | 'yellow';

interface Category {
  name: string;
  color: CategoryColor;
}

interface VendorCardProps {
  image: string;
  name: string;
  description: string;
  location: string;
  categories?: Category[];
  lastUpdated?: string;
  href?: string;
  imageWidth?: number | null;
  imageHeight?: number | null;
}

const categoryStyles: Record<CategoryColor, string> = {
  green: 'bg-soft-green text-green-600',
  pink: 'bg-soft-pink text-pink-600',
  purple: 'bg-soft-purple text-purple-600',
  blue: 'bg-soft-blue text-blue-600',
  yellow: 'bg-soft-yellow text-yellow-600',
};

const VendorCard = ({
  image,
  name,
  description,
  location,
  categories,
  lastUpdated,
  href = '#',
  imageWidth,
  imageHeight,
}: VendorCardProps) => {
  // Calculate aspect ratio from stored dimensions
  const aspectRatio = imageWidth && imageHeight ? imageWidth / imageHeight : 16 / 9;

  return (
    <Link to={href} className="break-inside-avoid mb-6 group bg-white rounded-4xl overflow-hidden border border-slate-100 hover:shadow-2xl hover:shadow-slate-200 transition-all duration-500 block cursor-pointer">
      <div className="relative overflow-hidden">
        {image ? (
          <div 
            className="w-full overflow-hidden"
            style={{ aspectRatio: aspectRatio }}
          >
            <img
              alt={name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              src={getResponsiveImage(image, imageWidth, imageHeight, 'card')}
              loading="lazy"
            />
          </div>
        ) : (
          <div className="w-full aspect-video bg-slate-100 flex items-center justify-center">
            <span className="material-symbols-outlined text-slate-300 text-4xl">image</span>
          </div>
        )}
        {categories && categories.length > 0 && (
          <div className="absolute top-4 left-4 flex flex-wrap gap-1.5">
            {categories.map((cat, index) => (
              <span
                key={index}
                className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${categoryStyles[cat.color]}`}
              >
                {cat.name}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="p-5">
        {lastUpdated && (
          <div className="flex items-center gap-1 mb-1.5">
            <span className="material-symbols-outlined text-slate-400 text-sm">update</span>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Aktualizováno {lastUpdated}
            </span>
          </div>
        )}
        <h3 className="text-lg font-extrabold mb-1.5 text-brand-dark group-hover:text-primary transition-colors">
          {name}
        </h3>
        <p className="text-slate-500 text-[13px] leading-snug mb-3 line-clamp-2">{description}</p>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-400">
            <span className="material-symbols-outlined text-sm">location_on</span> {location}
          </span>
          <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-50 text-slate-400 group-hover:bg-primary group-hover:text-brand-dark transition-all">
            <span className="material-symbols-outlined text-base">arrow_forward</span>
          </span>
        </div>
      </div>
    </Link>
  );
};

export default VendorCard;
